import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronDown, Search, Mail } from "lucide-react";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const CATEGORIES = [
  {
    id: "products",
    label: "Products & Quality",
    faqs: [
      {
        q: "What are peptides?",
        a: "Peptides are short chains of amino acids (typically 2–50) that act as signaling molecules in biological systems. They are widely studied for their role in cellular processes, regeneration, metabolism, and immune modulation. All EndaG1 peptides are intended solely for laboratory research use.",
      },
      {
        q: "What purity level do you provide?",
        a: "All EndaG1 peptides are ≥ 98% pure by HPLC (typically 99%+). Each batch is independently verified via HPLC and mass spectrometry. A Certificate of Analysis (COA) is available for every batch on request.",
      },
      {
        q: "How should peptides be stored?",
        a: "Lyophilized peptides should be stored in a cool, dry, dark environment (ideally at 2–8°C or frozen for long-term storage). Once reconstituted with bacteriostatic water, store refrigerated (2–8°C) and use within the timeframe indicated on the product's COA. Avoid repeated freeze-thaw cycles.",
      },
      {
        q: "How do I reconstitute a lyophilized peptide?",
        a: "Standard reconstitution uses sterile bacteriostatic water (0.9% benzyl alcohol) added slowly along the vial wall — do not inject directly onto the lyophilized powder. Swirl gently until fully dissolved. Do not shake vigorously. Reconstitution volume depends on your target concentration; consult the COA for guidance.",
      },
      {
        q: "Are your products intended for human use?",
        a: "No. EndaG1 products are strictly for laboratory research use only. They are not intended for human or veterinary consumption, medical use, therapeutic use, or any recreational purpose. See our full Disclaimer for details.",
      },
    ],
  },
  {
    id: "orders",
    label: "Orders & Account",
    faqs: [
      {
        q: "Do I need to create an account to order?",
        a: "No. You can check out as a guest with just an email and shipping address. Creating an account is optional but gives you order tracking, saved addresses, rewards points, and faster reorders.",
      },
      {
        q: "Can I modify or cancel my order?",
        a: "Orders can be modified or cancelled only before they have been dispatched. Please contact us as soon as possible with your order reference number. Once an order has shipped it can no longer be changed or cancelled.",
      },
      {
        q: "Where can I find my order status?",
        a: "If you created an account, sign in and visit the Orders tab in your account dashboard. Guests receive tracking information via email once their order ships.",
      },
      {
        q: "Can I get an invoice for my order?",
        a: "Yes. Signed-in customers can download a PDF invoice from the Orders tab in their account. Guests can request one by contacting us with their order reference.",
      },
    ],
  },
  {
    id: "payment",
    label: "Payment",
    faqs: [
      {
        q: "What payment methods do you accept?",
        a: "We accept credit and debit cards (via Stripe), PayPal, and Interac e-Transfer (Canadian orders only). All card payments are processed securely — we do not store card information on our servers.",
      },
      {
        q: "How does Interac e-Transfer work?",
        a: "After placing your order you'll receive a reference number and instructions. Send an e-Transfer to the address provided, including your reference in the message field. Once payment is confirmed on our end, your order will be dispatched.",
      },
      {
        q: "Is my payment information secure?",
        a: "Yes. All card payments are processed by PCI-DSS-compliant third-party processors (Stripe). EndaG1 does not directly handle or store credit card information. See our Privacy Statement for details.",
      },
      {
        q: "In what currency are prices displayed?",
        a: "Prices are displayed in USD by default. Canadian e-Transfer orders are settled in CAD.",
      },
    ],
  },
  {
    id: "shipping",
    label: "Shipping & Delivery",
    faqs: [
      {
        q: "How long does shipping take?",
        a: "Orders are typically processed and dispatched within 1–3 business days. Canadian orders arrive in 2–10 business days; US orders in 4–10 business days; international orders in 7–21 business days. See our full Shipping Terms for details.",
      },
      {
        q: "Do you ship internationally?",
        a: "Yes, we ship worldwide. Customers are responsible for ensuring products may lawfully be imported into their jurisdiction and for any customs duties, taxes, or handling fees.",
      },
      {
        q: "Is packaging discreet?",
        a: "Yes. All orders ship in plain, unmarked packaging.",
      },
      {
        q: "What if my package is lost or damaged?",
        a: "Contact us within 7 days of the expected delivery date. We'll work with the carrier to file a claim and resolve the issue. See our Shipping Terms for full details.",
      },
    ],
  },
  {
    id: "policies",
    label: "Returns & Policies",
    faqs: [
      {
        q: "Do you accept returns?",
        a: "No. Due to the sensitive nature of our research products, all sales are final. We do not accept returns or exchanges. If your order arrives damaged or incorrect, contact us within 7 days of delivery.",
      },
      {
        q: "What is your refund policy?",
        a: "Refunds are considered only for confirmed shipping errors, defective products, or damage in transit — reported within 7 days of delivery. Approved cases receive either a replacement or store credit at our discretion.",
      },
      {
        q: "How do I earn and redeem rewards points?",
        a: "Signed-in customers automatically earn 1 point per $1 spent on paid orders. Points appear in the Rewards tab of your account. Redemption details are shown alongside your balance.",
      },
    ],
  },
];

function FaqItem({ q, a, open, onToggle, testId }) {
  return (
    <div className="border-b border-white/10 last:border-b-0">
      <button
        data-testid={testId}
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 py-5 text-left group"
      >
        <span className="font-display text-[15px] sm:text-base text-[#F5F5F5] group-hover:text-[#4ADE80] transition-colors leading-snug">{q}</span>
        <ChevronDown className={`h-5 w-5 text-[#A8A8A8] shrink-0 mt-0.5 transition-transform duration-300 ${open ? "rotate-180 text-[#4ADE80]" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-6 pr-8 text-[14px] leading-[1.75] text-[#C9C9C9]">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [openKey, setOpenKey] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const allFaqs = CATEGORIES.flatMap((c) => c.faqs);
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const q = query.trim().toLowerCase();
  const filtered = CATEGORIES.map((c) => ({
    ...c,
    faqs: q ? c.faqs.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)) : c.faqs,
  })).filter((c) => c.faqs.length > 0);

  return (
    <div className="pt-20 min-h-screen flex flex-col bg-[#0F0F10] text-[#E5E5E5]">
      <SEO
        title="Frequently Asked Questions"
        description="Answers to the most common questions about EndaG1 research peptides — purity, COAs, storage, reconstitution, ordering, shipping, and payment options."
        canonicalPath="/faq"
        jsonLd={[faqSchema]}
      />
      <section className="mx-auto max-w-[960px] w-full px-5 sm:px-8 py-14 sm:py-20 flex-1">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#A8A8A8] hover:text-white transition-colors mb-8" data-testid="faq-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#4ADE80]">Support</p>
          <h1 className="mt-4 font-serif-editorial text-4xl sm:text-5xl md:text-6xl text-[#F5F5F5] leading-tight">Frequently Asked Questions</h1>
          <p className="mt-4 text-sm text-[#A8A8A8] max-w-xl">Everything about products, orders, shipping, and policies. Can&apos;t find an answer? <Link to="/#contact" className="text-[#4ADE80] hover:text-[#6FE998]">Get in touch</Link>.</p>
        </motion.div>

        {/* Search */}
        <div className="mt-10 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7a7a]" />
          <input
            data-testid="faq-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions…"
            className="w-full bg-[#171717] border border-white/10 pl-12 pr-4 py-3.5 text-sm outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a]"
          />
        </div>

        {/* Category chips */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="faq-categories">
          {CATEGORIES.map((c) => (
            <a key={c.id} href={`#${c.id}`} className="text-[11px] uppercase tracking-[0.14em] px-4 py-2 border border-white/10 bg-[#171717] hover:border-[#4ADE80] hover:text-[#4ADE80] text-[#A8A8A8] transition-colors">{c.label}</a>
          ))}
        </div>

        {/* Categories & questions */}
        <div className="mt-12 space-y-12">
          {filtered.length === 0 ? (
            <div className="border border-white/10 bg-[#171717] p-12 text-center">
              <p className="text-[#A8A8A8]">No results for &ldquo;{query}&rdquo;.</p>
              <p className="mt-2 text-xs text-[#7a7a7a]">Try a different search or <Link to="/#contact" className="text-[#4ADE80] hover:text-[#6FE998]">contact us</Link>.</p>
            </div>
          ) : filtered.map((cat) => (
            <section key={cat.id} id={cat.id} data-testid={`faq-category-${cat.id}`} className="scroll-mt-24">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-[11px] font-mono text-[#4ADE80] uppercase tracking-[0.16em]">{cat.label}</span>
                <span className="text-[10px] text-[#7a7a7a]">({cat.faqs.length})</span>
              </div>
              <div className="border border-white/10 bg-[#171717] px-6">
                {cat.faqs.map((f, i) => {
                  const key = `${cat.id}-${i}`;
                  return (
                    <FaqItem
                      key={key}
                      q={f.q}
                      a={f.a}
                      open={openKey === key}
                      onToggle={() => setOpenKey(openKey === key ? null : key)}
                      testId={`faq-question-${cat.id}-${i}`}
                    />
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-20 border border-white/10 bg-gradient-to-br from-[#171717] to-[#0F0F10] p-8 sm:p-10 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#4ADE80]/10 blur-[80px]" />
          <div className="relative">
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#4ADE80]">Still have questions?</p>
            <h2 className="mt-3 font-serif-editorial text-3xl sm:text-4xl text-[#F5F5F5]">Speak with the lab.</h2>
            <p className="mt-3 text-sm text-[#A8A8A8] max-w-lg">Our team responds within one business day. Reach us via the contact form or open a support ticket from your account.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/#contact" data-testid="faq-contact-form-link" className="inline-flex items-center gap-2 bg-[#4ADE80] text-[#0F0F10] px-6 py-3 uppercase tracking-[0.14em] text-[11px] hover:bg-[#6FE998] transition-colors">
                <Mail className="h-4 w-4" /> Contact Us
              </Link>
              <Link to="/account?tab=support" className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F5] px-6 py-3 uppercase tracking-[0.14em] text-[11px] hover:bg-white/5 transition-colors">Open a Ticket</Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
